import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useCreateClient } from "@/hooks/useCreateClient";
import { useMe } from "@/hooks/useMe";
import { showMutationError } from "@/lib/mutation-error";
import type { PartnerBillingMode } from "@/types/partner.types";

export const NewClientPage = () => {
	const navigate = useNavigate();
	const { data: me } = useMe();
	const createClient = useCreateClient();

	const [name, setName] = useState("");
	const [ownerEmail, setOwnerEmail] = useState("");
	const [ownerFirstName, setOwnerFirstName] = useState("");
	const [ownerLastName, setOwnerLastName] = useState("");
	const [billingMode, setBillingMode] = useState<PartnerBillingMode | "">("");

	const effectiveBillingMode: PartnerBillingMode =
		billingMode || me?.partner.defaultBillingMode || "client";

	const canSubmit =
		name.trim() &&
		ownerEmail.trim() &&
		ownerFirstName.trim() &&
		ownerLastName.trim() &&
		!createClient.isPending;

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!canSubmit) return;
		createClient.mutate(
			{
				name: name.trim(),
				ownerEmail: ownerEmail.trim(),
				ownerFirstName: ownerFirstName.trim(),
				ownerLastName: ownerLastName.trim(),
				billingMode: effectiveBillingMode,
			},
			{
				onSuccess: (team) => {
					toast.success("Client account created");
					navigate(`/clients/${team.id}`);
				},
				onError: (err) => {
					showMutationError(err, "Failed to create the client account");
				},
			},
		);
	};

	return (
		<div className="space-y-4 max-w-2xl">
			<Link
				to="/clients"
				className="inline-flex items-center gap-1 text-sm text-clyr-gray-400 hover:text-white transition-colors"
			>
				<ArrowLeft className="h-4 w-4" />
				Back to clients
			</Link>

			<Card className="bg-clyr-gray-800 rounded-lg border-clyr-gray-700">
				<CardHeader>
					<CardTitle className="text-white">New client</CardTitle>
					<CardDescription className="text-clyr-gray-400">
						Provision a Clyr account for a client. New accounts await Clyr
						activation before the client can sign in.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="client-name" className="text-clyr-gray-300">
								Company name
							</Label>
							<Input
								id="client-name"
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="Acme Property Management"
								className="bg-clyr-gray-900 border-clyr-gray-700 text-white placeholder:text-clyr-gray-500"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="owner-email" className="text-clyr-gray-300">
								Owner email
							</Label>
							<Input
								id="owner-email"
								type="email"
								value={ownerEmail}
								onChange={(e) => setOwnerEmail(e.target.value)}
								placeholder="owner@acme.com"
								className="bg-clyr-gray-900 border-clyr-gray-700 text-white placeholder:text-clyr-gray-500"
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="owner-first" className="text-clyr-gray-300">
									Owner first name
								</Label>
								<Input
									id="owner-first"
									value={ownerFirstName}
									onChange={(e) => setOwnerFirstName(e.target.value)}
									className="bg-clyr-gray-900 border-clyr-gray-700 text-white"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="owner-last" className="text-clyr-gray-300">
									Owner last name
								</Label>
								<Input
									id="owner-last"
									value={ownerLastName}
									onChange={(e) => setOwnerLastName(e.target.value)}
									className="bg-clyr-gray-900 border-clyr-gray-700 text-white"
								/>
							</div>
						</div>
						<div className="space-y-2">
							<Label className="text-clyr-gray-300">Billing mode</Label>
							<Select
								value={effectiveBillingMode}
								onValueChange={(val) =>
									setBillingMode(val as PartnerBillingMode)
								}
							>
								<SelectTrigger className="w-full bg-clyr-gray-900">
									<SelectValue placeholder="Select billing mode" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="partner">
										Partner pays — billed to your firm
									</SelectItem>
									<SelectItem value="client">
										Client pays — billed to the client directly
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="flex justify-end gap-2 pt-2">
							<Button
								type="button"
								variant="secondary"
								onClick={() => navigate("/clients")}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={!canSubmit}>
								{createClient.isPending ? "Creating..." : "Create client"}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</div>
	);
};
